import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useDemo } from '../context/DemoContext';
import { api } from '../api';
import {
  LayoutDashboard, Users, Building2, Package, Inbox,
  Globe, Sparkles, Settings, Search,
  LogOut, Menu, X, Languages, ShoppingCart,
  FlaskConical, ShieldCheck, TrendingUp,
  FileText, Compass, Plus,
} from 'lucide-react';
import PageTransition from './PageTransition';

// ── Nav config ────────────────────────────────────────────────────────────

type NavSubItem = { path: string; label: string; icon: React.ReactNode };
type NavItem = {
  path: string; label: string; icon: React.ReactNode;
  badge?: string;
  children?: NavSubItem[];
};

const adminNav: NavItem[] = [
  { path: '/admin/dashboard',     label: 'Dashboard',     icon: <LayoutDashboard className="w-4 h-4 shrink-0" /> },
  {
    path: '/admin/creator-match', label: 'Creator Match', icon: <Sparkles className="w-4 h-4 shrink-0" />, badge: 'AI',
    children: [
      { path: '/admin/explore', label: 'Explore Influencers', icon: <Users className="w-4 h-4 shrink-0" /> },
    ],
  },
  { path: '/admin/influencers',   label: 'Influencers',   icon: <Users className="w-4 h-4 shrink-0" /> },
  { path: '/admin/vendors',          label: 'Vendors',          icon: <Building2 className="w-4 h-4 shrink-0" /> },
  { path: '/admin/products',          label: 'Products',         icon: <Package className="w-4 h-4 shrink-0" /> },
  { path: '/admin/requests',      label: 'Requests',      icon: <Inbox className="w-4 h-4 shrink-0" /> },
  { path: '/admin/websites',      label: 'Websites',      icon: <Globe className="w-4 h-4 shrink-0" /> },
  { path: '/admin/manage-users',  label: 'Manage Users',  icon: <ShieldCheck className="w-4 h-4 shrink-0" /> },
];

const influencerNav: NavItem[] = [
  { path: '/dashboard',    label: 'Overview',    icon: <LayoutDashboard className="w-4 h-4 shrink-0" /> },
  { path: '/reports',      label: 'My Reports',  icon: <FileText className="w-4 h-4 shrink-0" /> },
  { path: '/reports/new',  label: 'New Report',  icon: <Plus className="w-4 h-4 shrink-0" />, badge: 'AI' },
  {
    path: '/explore', label: 'Storefront', icon: <Compass className="w-4 h-4 shrink-0" />,
    children: [
      { path: '/explore',   label: 'Explore products', icon: <Search className="w-4 h-4 shrink-0" /> },
      { path: '/products',  label: 'My products',      icon: <Package className="w-4 h-4 shrink-0" /> },
      { path: '/requests',  label: 'My requests',      icon: <Inbox className="w-4 h-4 shrink-0" /> },
      { path: '/your-site', label: 'Your site',        icon: <Globe className="w-4 h-4 shrink-0" /> },
    ],
  },
  { path: '/upgrade',  label: 'Upgrade',  icon: <Sparkles className="w-4 h-4 shrink-0" />, badge: 'PRO' },
  { path: '/settings', label: 'Settings', icon: <Settings className="w-4 h-4 shrink-0" /> },
];

const vendorNavKeys: Array<{ path: string; labelKey: string; icon: React.ReactNode; badge?: string }> = [
  { path: '/vendor/dashboard',         labelKey: 'nav.dashboard',   icon: <LayoutDashboard className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/creators',          labelKey: 'nav.creators',    icon: <Users className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/creator-insights',  labelKey: 'nav.insights',    icon: <Sparkles className="w-4 h-4 shrink-0" />, badge: 'AI' },
  { path: '/vendor/forecasts',         labelKey: 'nav.forecasts',   icon: <TrendingUp className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/products',          labelKey: 'nav.products',    icon: <Package className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/orders',            labelKey: 'nav.orders',      icon: <ShoppingCart className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/payouts',           labelKey: 'nav.payouts',     icon: <Building2 className="w-4 h-4 shrink-0" /> },
  { path: '/vendor/settings',          labelKey: 'nav.settings',    icon: <Settings className="w-4 h-4 shrink-0" /> },
];

// ── NavLink / NavGroup ────────────────────────────────────────────────────
//
// Module-level components (stable references across Layout re-renders).
// Styling is entirely CSS-driven — the sidebar's :hover cascade handles
// collapsed ↔ expanded transitions without any JS state.

interface NavLinkProps {
  item: NavItem | NavSubItem;
  onClick?: () => void;
  sub?: boolean;  // true → render as indented sub-item
}

function NavLink({ item, onClick, sub = false }: NavLinkProps) {
  const location = useLocation();
  const active =
    item.path === location.pathname ||
    location.pathname.startsWith(item.path + '/');

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`sb-nav-link${active ? ' active' : ''}${sub ? ' sb-sub-link' : ''}`}
    >
      <span className="sb-nav-icon shrink-0">{item.icon}</span>
      <span className="sb-label text-[13px] font-medium">{item.label}</span>
      {'badge' in item && item.badge && (
        <span
          className="sb-badge text-[9px] font-bold ml-1.5 px-1 py-0.5"
          style={{ backgroundColor: 'rgba(255,212,0,0.12)', color: '#FFD400', borderRadius: 2, letterSpacing: '0.06em' }}
        >
          {item.badge}
        </span>
      )}
    </Link>
  );
}

// NavGroup renders a parent nav item plus its indented children.
// Children are revealed via CSS when the sidebar is hovered (desktop) or
// always visible on mobile. The .sb-subnav wrapper uses the same
// max-height/opacity animation pattern as the rest of the sidebar.
function NavGroup({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  if (!item.children?.length) {
    return <NavLink item={item} onClick={onClick} />;
  }
  return (
    <div>
      <NavLink item={item} onClick={onClick} />
      <div className="sb-subnav">
        {item.children.map(child => (
          <NavLink key={child.path} item={child} onClick={onClick} sub />
        ))}
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { isDemoMode, toggleDemoMode } = useDemo();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['vendor', 'common']);

  // Mobile drawer only — desktop hover is handled entirely by CSS
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const isAdmin   = user?.role === 'admin';
  const isVendor  = user?.role === 'vendor';

  const vendorNav: NavItem[] = vendorNavKeys.map(k => ({
    path: k.path,
    label: t(`vendor:${k.labelKey}`, k.labelKey.split('.').pop() || k.path),
    icon: k.icon,
    badge: k.badge,
  }));

  const navItems  = isAdmin ? adminNav : isVendor ? vendorNav : influencerNav;
  const homeRoute = isAdmin ? '/admin/dashboard' : isVendor ? '/vendor/dashboard' : '/dashboard';
  const initial   = user?.email?.charAt(0).toUpperCase() ?? '?';
  const roleLabel = isAdmin ? 'Admin' : isVendor ? t('vendor:roleLabel') : 'Creator';

  const handleLangToggle = async () => {
    const next = i18n.language === 'zh' ? 'en' : 'zh';
    i18n.changeLanguage(next);
    if (isVendor) {
      try { await api.updateVendorLocale(next); } catch { /* non-critical */ }
    }
  };

  // ── Desktop sidebar ────────────────────────────────────────────────────
  //
  // Width is animated purely via CSS (.sidebar-desktop { width: 62px } and
  // .sidebar-desktop:hover { width: 224px }) — no JS state, no framer-motion.
  // This is immune to React re-render / navigation spurious mouseleave events.

  const DesktopSidebar = (
    <aside
      className="sidebar-desktop hidden lg:flex flex-col shrink-0 overflow-hidden border-r z-40"
      style={{
        height: '100vh',
        position: 'sticky',
        top: 0,
        backgroundColor: 'var(--ch2-surface)',
        borderColor: 'var(--ch2-border)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center h-[62px] shrink-0 overflow-hidden border-b"
        style={{ borderColor: 'var(--ch2-border)', paddingLeft: '20px' }}
      >
        <Link to={homeRoute} className="flex items-center">
          <span
            className="font-display font-bold text-sm shrink-0 flex items-center justify-center relative"
            style={{
              color: '#FFD400',
              background: 'var(--ch2-ink)',
              borderRadius: 5,
              width: 22,
              height: 22,
              fontSize: 12,
              letterSpacing: '-0.04em',
            }}
          >
            C
          </span>
          <span
            className="sb-logo-text font-display font-bold text-sm"
            style={{ letterSpacing: '-0.015em', marginLeft: 8 }}
          >
            <span style={{ color: 'var(--ch2-ink)' }}>CreatorHub</span>
            <span style={{ color: 'var(--ch2-accent)' }}>360</span>
          </span>
        </Link>
      </div>

      {/* Role chip */}
      <div className="sb-role-chip" style={{ paddingLeft: '20px' }}>
        <div style={{ paddingTop: 14, paddingBottom: 4 }}>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.1em] whitespace-nowrap"
            style={{ color: 'var(--ch2-ink-3)' }}
          >
            {roleLabel}
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden">
        {navItems.map(item => (
          <NavGroup key={item.path} item={item} />
        ))}
      </nav>

      {/* User section */}
      <div
        className="shrink-0 px-2 py-3 border-t space-y-0.5 overflow-hidden"
        style={{ borderColor: 'var(--ch2-border)' }}
      >
        {/* Avatar + email */}
        <div className="flex items-center py-2.5" style={{ paddingLeft: '16px' }}>
          <div
            className="w-6 h-6 flex items-center justify-center shrink-0 text-[11px] font-bold"
            style={{
              backgroundColor: 'var(--ch2-accent-tint)',
              color: 'var(--ch2-accent-2)',
              borderRadius: '4px',
              border: '1px solid var(--ch2-border)',
            }}
          >
            {initial}
          </div>
          <div className="sb-user-info min-w-0">
            <p className="text-xs font-medium truncate" style={{ maxWidth: 140, color: 'var(--ch2-ink)' }}>{user?.email}</p>
            <p className="text-[10px] capitalize" style={{ color: 'var(--ch2-ink-3)' }}>{user?.role}</p>
          </div>
        </div>

        {/* Language toggle (vendor only) */}
        {isVendor && (
          <button
            onClick={handleLangToggle}
            className="w-full flex items-center py-2.5 transition-colors"
            style={{ paddingLeft: '18px', borderRadius: '2px', color: 'var(--ch2-ink-2)' }}
          >
            <Languages className="w-4 h-4 shrink-0" />
            <span className="sb-sign-out-label text-[13px] font-medium">
              {i18n.language === 'zh' ? 'English' : '中文'}
            </span>
          </button>
        )}

        {/* Demo mode toggle — icon only; accent when active */}
        <button
          type="button"
          onClick={toggleDemoMode}
          className="w-full flex items-center py-2.5 transition-colors hover:opacity-90"
          style={{
            paddingLeft: '18px',
            borderRadius: '2px',
            color: isDemoMode ? 'var(--ch2-accent)' : 'var(--ch2-ink-3)',
          }}
          title={isDemoMode ? 'Exit demo mode' : 'Enter demo mode (fake data)'}
        >
          <FlaskConical className="w-4 h-4 shrink-0" />
        </button>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center py-2.5 transition-colors"
          style={{ paddingLeft: '18px', borderRadius: '2px', color: 'var(--ch2-ink-2)' }}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span className="sb-sign-out-label text-[13px] font-medium">{isVendor ? t('common:signOut') : 'Sign Out'}</span>
        </button>
      </div>
    </aside>
  );

  // ── Mobile sidebar ─────────────────────────────────────────────────────

  const MobileSidebar = (
    <>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            key="drawer"
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col lg:hidden border-r"
            style={{ backgroundColor: 'var(--ch2-surface)', borderColor: 'var(--ch2-border)' }}
          >
            <div
              className="flex items-center justify-between px-5 h-[62px] border-b shrink-0"
              style={{ borderColor: 'var(--ch2-border)' }}
            >
              <Link
                to={homeRoute}
                className="flex items-baseline"
                onClick={() => setMobileOpen(false)}
              >
                <span className="font-display font-bold text-sm" style={{ letterSpacing: '-0.015em', color: 'var(--ch2-ink)' }}>
                  CreatorHub
                </span>
                <span className="font-display font-bold text-sm" style={{ color: 'var(--ch2-accent)' }}>360</span>
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 transition-colors"
                style={{ color: 'var(--ch2-ink-3)' }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pt-4 pb-1 shrink-0">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--ch2-ink-3)' }}>
                {roleLabel}
              </span>
            </div>

            <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
              {navItems.map(item => (
                <NavGroup key={item.path} item={item} onClick={() => setMobileOpen(false)} />
              ))}
            </nav>

            <div className="px-2 py-3 border-t space-y-0.5" style={{ borderColor: 'var(--ch2-border)' }}>
              <div className="flex items-center gap-3 px-4 py-2.5">
                <div
                  className="w-6 h-6 flex items-center justify-center shrink-0 text-[11px] font-bold"
                  style={{ backgroundColor: 'var(--ch2-accent-tint)', color: 'var(--ch2-accent-2)', borderRadius: '4px', border: '1px solid var(--ch2-border)' }}
                >
                  {initial}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: 'var(--ch2-ink)' }}>{user?.email}</p>
                  <p className="text-[10px] capitalize" style={{ color: 'var(--ch2-ink-3)' }}>{user?.role}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { toggleDemoMode(); setMobileOpen(false); }}
                className="w-full flex items-center px-4 py-2.5 transition-colors hover:opacity-90"
                style={{
                  borderRadius: '2px',
                  color: isDemoMode ? 'var(--ch2-accent)' : 'var(--ch2-ink-3)',
                }}
                title={isDemoMode ? 'Exit demo mode' : 'Enter demo mode (fake data)'}
              >
                <FlaskConical className="w-4 h-4 shrink-0" />
              </button>
              <button
                onClick={() => { setMobileOpen(false); void handleLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-[13px]"
                style={{ borderRadius: '2px', color: 'var(--ch2-ink-2)' }}
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="theme-light min-h-screen flex" style={{ backgroundColor: 'var(--ch2-bg)' }}>
      {MobileSidebar}
      {DesktopSidebar}

      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Mobile topbar */}
        <header
          className="lg:hidden sticky top-0 z-30 border-b backdrop-blur-xl"
          style={{ backgroundColor: 'rgba(255,255,255,0.92)', borderColor: 'var(--ch2-border)' }}
        >
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1.5 transition-colors"
              style={{ color: 'var(--ch2-ink-2)' }}
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-display font-bold text-sm" style={{ color: 'var(--ch2-ink)' }}>
              CreatorHub<span style={{ color: 'var(--ch2-accent)' }}>360</span>
            </span>
            <div className="w-8" />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8 overflow-auto max-w-[1280px] w-full mx-auto">
          <PageTransition>
            {children}
          </PageTransition>
        </main>
      </div>

      {isDemoMode && (
        <button
          type="button"
          onClick={toggleDemoMode}
          title="Exit demo mode"
          className="fixed bottom-4 right-4 z-50 flex h-7 w-7 items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100"
          style={{ backgroundColor: 'rgba(255,212,0,0.15)' }}
        >
          <FlaskConical className="h-3 w-3 shrink-0" style={{ color: '#FFD400' }} />
        </button>
      )}
    </div>
  );
}
